import React, { useMemo, useRef } from "react";
import { useDroppable } from "@dnd-kit/core";
import { Task } from "../../types";
import TaskCard from "./TaskCard";
import BoardDropIndicator from "./BoardDropIndicator";
import { useFlipAnimation } from "../../hooks/useFlipAnimation";

interface BoardListlessAreaProps {
  tasks: Task[];
  matchingTaskIds: Set<string>;
  layout: "desktop" | "mobile";
  activeDragTask?: Task | null;
  overTaskId?: string | null;
  isOverEndZone?: boolean;
  exitAnimatingTaskId?: string | null;
  // Task ID currently highlighted by keyboard arrow navigation.
  focusedTaskId?: string | null;
  onToggleTask: (id: string) => void;
  onEditTask: (task: Task) => void;
  onConvertToSubtask?: (id: string) => void;
}

const BoardListlessArea: React.FC<BoardListlessAreaProps> = ({
  tasks,
  matchingTaskIds,
  layout,
  activeDragTask,
  overTaskId,
  isOverEndZone,
  exitAnimatingTaskId,
  focusedTaskId,
  onToggleTask,
  onEditTask,
  onConvertToSubtask,
}) => {
  const mobilePanelRef = useRef<HTMLDivElement>(null);

  // Render only the visible tasks. Unlike a list column (which keeps hidden
  // tasks in the DOM with a collapse animation, then drops the whole column
  // when empty), each listless task is its own masonry block — keeping
  // hidden ones around leaves their margins behind, which CSS columns then
  // distributes across the layout and pushes visible cards into the wrong
  // column. The parent masonry FLIP animates the resulting reflow.
  const visibleTasks = useMemo(
    () => tasks.filter((t) => matchingTaskIds.has(t.id)),
    [tasks, matchingTaskIds],
  );

  const syncFlipPositions = useFlipAnimation(
    mobilePanelRef,
    "[data-flip-id]",
    visibleTasks.map((t) => t.id).join(","),
  );

  const { setNodeRef: setEndZoneRef } = useDroppable({
    id: "end-zone-listless",
    data: { type: "end-zone", listId: null },
  });

  const renderTaskInner = (task: Task, index: number) => {
    const isOver = overTaskId === task.id && activeDragTask?.id !== task.id;
    const draggedIndex = activeDragTask
      ? visibleTasks.findIndex((t) => t.id === activeDragTask.id)
      : -1;
    const showAbove = isOver && (draggedIndex === -1 || draggedIndex > index);
    const showBelow = isOver && draggedIndex !== -1 && draggedIndex < index;

    return (
      <div data-flip-id={task.id}>
        {showAbove && <BoardDropIndicator />}
        <TaskCard
          task={task}
          viewMode="list"
          suppressTransform={!!activeDragTask}
          onToggle={onToggleTask}
          onEdit={onEditTask}
          onConvertToSubtask={onConvertToSubtask}
          onBeforeExit={syncFlipPositions}
          requestExit={exitAnimatingTaskId === task.id}
          isFocused={focusedTaskId === task.id}
        />
        {showBelow && <BoardDropIndicator />}
      </div>
    );
  };

  if (layout === "mobile") {
    return (
      <div ref={mobilePanelRef} className="space-y-2.5">
        {visibleTasks.map((task, index) => (
          <div key={task.id}>{renderTaskInner(task, index)}</div>
        ))}
        {isOverEndZone && <BoardDropIndicator />}
        <div ref={setEndZoneRef} className="h-8" />
      </div>
    );
  }

  // Desktop: each visible listless task is its own masonry block, tracked
  // by the parent columnContainerRef FLIP via data-flip-column-id.
  return (
    <>
      {visibleTasks.map((task, index) => (
        <div
          key={task.id}
          data-flip-column-id={`listless-${task.id}`}
          style={{ breakInside: "avoid" }}
          className="mb-2.5"
        >
          {renderTaskInner(task, index)}
        </div>
      ))}
      {isOverEndZone && (
        <div
          data-flip-column-id="listless-end-indicator"
          style={{ breakInside: "avoid" }}
          className="mb-2.5"
        >
          <BoardDropIndicator />
        </div>
      )}
      <div
        ref={setEndZoneRef}
        data-flip-column-id="listless-end-zone"
        style={{ breakInside: "avoid" }}
        className="h-8"
      />
    </>
  );
};

export default BoardListlessArea;
